import classNames from 'classnames'
import { forwardRef, ReactNode } from 'react'
import { mergeRefs } from 'react-merge-refs'
import { Color } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '../../../Icon'
import { singleSelectClasses } from '../componentClassNames'
import { useSingleSelectSearch, useSingleSelectState } from '../contexts'
import * as styles from './styles'

type TriggerProps = {
  ariaLabel?: string
  children: ReactNode
  className?: string
  disabled?: boolean
  hasError?: boolean
  hideLeadingNode?: boolean
  hideTrailingNode?: boolean
  htmlId?: string
  inputWidth?: string | number
  tabIndex?: number
}

/**
 * The main button that opens the dropdown menu. This component
 * is sepficially configured to accept Tooltip props from Radix.
 * If a `Tooltip` is being used, it should be the **parent** component
 * to the `Trigger`.
 */
export const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  (props, tooltipRef) => {
    const theme = useTheme()
    const {
      ariaLabel = 'Open select menu...',
      children,
      className,
      disabled,
      hasError = false,
      htmlId,
      hideLeadingNode = false,
      hideTrailingNode = false,
      inputWidth = '100%',
      tabIndex,
      ...radixProps
    } = props
    const {
      componentId,
      getToggleButtonProps,
      hasLabel,
      isOpen,
      onBlur,
      selectedItem,
      toggleRef,
    } = useSingleSelectState()
    const { searchFocused, searchRef } = useSingleSelectSearch()

    const onMenuBlur = (e: React.FocusEvent) => {
      if (e.relatedTarget !== searchRef?.current) {
        onBlur?.()
      }
    }

    return (
      <button
        type="button"
        className={classNames(singleSelectClasses.trigger, className, {
          hasSelection: Boolean(selectedItem),
        })}
        css={styles.inputContainer({ isOpen, hasError })}
        style={{ width: inputWidth }}
        {...getToggleButtonProps({
          disabled,
          tabIndex,
          id: htmlId,
          ref: mergeRefs([toggleRef, tooltipRef]),
          onBlur: onMenuBlur,
          // Downshift closes the menu on this Escape. A container that waits
          // for Escape to bubble (Block details panel) must not also close.
          onKeyDown: (e) => {
            if (e.key === 'Escape' && isOpen) e.stopPropagation()
          },
          // Manually set aria-activedescendant to search input when focused
          // Don't set otherwise, so Downshift can handle the list items
          ...(searchFocused
            ? { 'aria-activedescendant': `${componentId}-search` }
            : {}),
          ...(!hasLabel && {
            'aria-label': ariaLabel,
          }),
        })}
        {...radixProps}
      >
        <div css={styles.selectionContent}>
          {hideLeadingNode ? null : selectedItem?.leadingNode}
          {children}
          {hideTrailingNode ? null : selectedItem?.trailingNode}
        </div>
        <Icon
          glyph="gem-chevron-s"
          className="single-select-toggle"
          ariaHidden
          color={Color[theme]['Text/Primary']}
        />
      </button>
    )
  }
)

Trigger.displayName = 'Lapidary: Trigger ← SingleSelect'
